// =============================================================================
// RELATION EDGE - Custom React Flow edge for entity relations
// =============================================================================

import { memo } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type EdgeProps,
} from '@xyflow/react';
import { X } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import type { RelationType } from '../../types';

interface RelationEdgeData {
  label?: string;
  relationType: RelationType;
  onDelete?: (id: string) => void;
}

const RELATION_LABELS: Record<RelationType, { source: string; target: string }> = {
  'one-to-one': { source: '1', target: '1' },
  'one-to-many': { source: '1', target: '*' },
  'many-to-one': { source: '*', target: '1' },
  'many-to-many': { source: '*', target: '*' },
};

const RELATION_COLORS: Record<RelationType, string> = {
  'one-to-one': '#3B82F6',
  'one-to-many': '#10B981',
  'many-to-one': '#F59E0B',
  'many-to-many': '#8B5CF6',
};

function RelationEdgeComponent({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
  style = {},
}: EdgeProps) {
  const edgeData = data as RelationEdgeData | undefined;
  const relationType = edgeData?.relationType || 'one-to-many';
  const color = RELATION_COLORS[relationType];
  const labels = RELATION_LABELS[relationType];

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    curvature: 0.25,
  });

  return (
    <>
      {/* Main edge path */}
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          ...style,
          stroke: selected ? color : `${color}80`,
          strokeWidth: selected ? 3 : 2,
          strokeDasharray: relationType === 'many-to-many' ? '5,5' : undefined,
        }}
        className="transition-all"
      />

      {/* Edge decorators */}
      <EdgeLabelRenderer>
        {/* Source cardinality marker */}
        <div
          className="absolute pointer-events-none"
          style={{
            transform: `translate(-50%, -50%) translate(${sourceX}px, ${sourceY - 15}px)`,
          }}
        >
          <span
            className={cn(
              'text-xs font-bold px-1.5 py-0.5 rounded',
              selected
                ? 'bg-white dark:bg-secondary-800 shadow-md'
                : 'bg-secondary-100 dark:bg-secondary-700'
            )}
            style={{ color }}
          >
            {labels.source}
          </span>
        </div>

        {/* Target cardinality marker */}
        <div
          className="absolute pointer-events-none"
          style={{
            transform: `translate(-50%, -50%) translate(${targetX}px, ${targetY + 15}px)`,
          }}
        >
          <span
            className={cn(
              'text-xs font-bold px-1.5 py-0.5 rounded',
              selected
                ? 'bg-white dark:bg-secondary-800 shadow-md'
                : 'bg-secondary-100 dark:bg-secondary-700'
            )}
            style={{ color }}
          >
            {labels.target}
          </span>
        </div>

        {/* Relation label (centered) */}
        <div
          className="absolute nodrag nopan"
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
          }}
        >
          <div
            className={cn(
              'flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
              'bg-white dark:bg-secondary-800 border shadow-sm transition-all',
              selected
                ? 'border-2 shadow-md'
                : 'border-secondary-200 dark:border-secondary-600'
            )}
            style={{ borderColor: selected ? color : undefined }}
          >
            <span style={{ color }}>{edgeData?.label || relationType}</span>
            {edgeData?.onDelete && selected && (
              <button
                onClick={() => edgeData.onDelete?.(id)}
                className="p-0.5 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 text-secondary-400 hover:text-red-600 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      </EdgeLabelRenderer>

      {/* Animated flow indicator */}
      {selected && (
        <circle r="4" fill={color}>
          <animateMotion dur="2s" repeatCount="indefinite" path={edgePath} />
        </circle>
      )}
    </>
  );
}

export const RelationEdge = memo(RelationEdgeComponent);
